import React, { createContext, useContext, useState, useEffect } from "react";

export type Language = "en" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

// Translation data
const translations = {
  en: {
    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.income": "Income",
    "nav.expenses": "Expenses",
    "nav.assets": "Assets & Debts",
    "nav.projections": "Projections",
    "nav.fire": "FIRE",
    "nav.partner": "Partner",
    "nav.settings": "Settings",

    // Common
    "common.add": "Add",
    "common.edit": "Edit",
    "common.delete": "Delete",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.yes": "Yes",
    "common.no": "No",
    "common.loading": "Loading...",
    "common.error": "Error",
    "common.success": "Success",

    // Dashboard
    "dashboard.welcome": "Welcome to Runway",
    "dashboard.description":
      "Track your financial runway and make informed decisions",
    "dashboard.addFirstTransaction": "Add Your First Transaction",
    "dashboard.monthlyIncome": "Monthly Income",
    "dashboard.monthlyExpenses": "Monthly Expenses",
    "dashboard.currentBalance": "Current Balance",
    "dashboard.runwayMonths": "Runway (Months)",

    // Income
    "income.title": "Income",
    "income.description": "Manage your income sources and transactions",
    "income.addIncome": "Add Income",
    "income.incomeSources": "Income Sources",
    "income.monthlyIncome": "Monthly Income",
    "income.noIncomeSources": "No Income Sources",
    "income.addFirstIncome": "Add Your First Income Source",

    // Expenses
    "expenses.title": "Expenses",
    "expenses.description": "Track your spending and manage expenses",
    "expenses.addExpense": "Add Expense",
    "expenses.monthlyExpenses": "Monthly Expenses",
    "expenses.expenseCategories": "Expense Categories",
    "expenses.thisMonth": "This Month",
    "expenses.transactions": "transactions",
    "expenses.noExpenses": "No expenses recorded yet",
    "expenses.addFirstExpense": "Add Your First Expense",

    // Assets
    "assets.title": "Assets & Debts",
    "assets.description": "Track your assets, investments, and debts",
    "assets.addAsset": "Add Asset",
    "assets.addDebt": "Add Debt",
    "assets.totalAssets": "Total Assets",
    "assets.totalDebts": "Total Debts",
    "assets.netWorth": "Net Worth",
    "assets.assetCount": "Asset Count",

    // Settings
    "settings.title": "Settings",
    "settings.description": "Manage your account and application preferences",
    "settings.language": "Language",
    "settings.demoMode": "Demo Mode",
    "settings.demoModeActive": "Demo Mode Active",
    "settings.demoModeDescription":
      "You're currently exploring the app with sample data. Your changes won't be saved and will reset when you close the browser.",
    "settings.exitDemoMode": "Exit Demo Mode",
    "settings.clearFinancialData": "Clear Financial Data",
    "settings.clearAllData": "Clear All Data",
    "settings.clearFinancialDataDescription":
      "This will clear all your financial data (transactions, income, expenses, assets, debts) but keep your user profile.",
    "settings.clearAllDataDescription":
      "This will clear all data including your user profile. You'll need to start over completely.",

    // FIRE
    "fire.title": "FIRE Calculator",
    "fire.description":
      "Calculate your path to Financial Independence, Retire Early (FIRE)",
    "fire.resetToDashboard": "Reset to Dashboard",

    // Partner
    "partner.title": "Partner Management",
    "partner.description": "Manage financial partners and collaborators",
    "partner.addPartner": "Add Partner",

    // Projections
    "projections.title": "Financial Projections",
    "projections.description":
      "Explore different financial scenarios and their impact on your runway",

    // Language names
    "language.english": "English",
    "language.spanish": "Español",
  },
  es: {
    // Navigation
    "nav.dashboard": "Panel",
    "nav.income": "Ingresos",
    "nav.expenses": "Gastos",
    "nav.assets": "Activos y Deudas",
    "nav.projections": "Proyecciones",
    "nav.fire": "FIRE",
    "nav.partner": "Socio",
    "nav.settings": "Configuración",

    // Common
    "common.add": "Agregar",
    "common.edit": "Editar",
    "common.delete": "Eliminar",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.close": "Cerrar",
    "common.yes": "Sí",
    "common.no": "No",
    "common.loading": "Cargando...",
    "common.error": "Error",
    "common.success": "Éxito",

    // Dashboard
    "dashboard.welcome": "Bienvenido a Runway",
    "dashboard.description":
      "Rastrea tu pista financiera y toma decisiones informadas",
    "dashboard.addFirstTransaction": "Agregar Tu Primera Transacción",
    "dashboard.monthlyIncome": "Ingresos Mensuales",
    "dashboard.monthlyExpenses": "Gastos Mensuales",
    "dashboard.currentBalance": "Balance Actual",
    "dashboard.runwayMonths": "Pista (Meses)",

    // Income
    "income.title": "Ingresos",
    "income.description": "Gestiona tus fuentes de ingresos y transacciones",
    "income.addIncome": "Agregar Ingreso",
    "income.incomeSources": "Fuentes de Ingresos",
    "income.monthlyIncome": "Ingresos Mensuales",
    "income.noIncomeSources": "Sin Fuentes de Ingresos",
    "income.addFirstIncome": "Agregar Tu Primera Fuente de Ingresos",

    // Expenses
    "expenses.title": "Gastos",
    "expenses.description": "Rastrea tus gastos y gestiona tus desembolsos",
    "expenses.addExpense": "Agregar Gasto",
    "expenses.monthlyExpenses": "Gastos Mensuales",
    "expenses.expenseCategories": "Categorías de Gastos",
    "expenses.thisMonth": "Este Mes",
    "expenses.transactions": "transacciones",
    "expenses.noExpenses": "Aún no hay gastos registrados",
    "expenses.addFirstExpense": "Agregar Tu Primer Gasto",

    // Assets
    "assets.title": "Activos y Deudas",
    "assets.description": "Rastrea tus activos, inversiones y deudas",
    "assets.addAsset": "Agregar Activo",
    "assets.addDebt": "Agregar Deuda",
    "assets.totalAssets": "Total de Activos",
    "assets.totalDebts": "Total de Deudas",
    "assets.netWorth": "Patrimonio Neto",
    "assets.assetCount": "Cantidad de Activos",

    // Settings
    "settings.title": "Configuración",
    "settings.description":
      "Gestiona tu cuenta y preferencias de la aplicación",
    "settings.language": "Idioma",
    "settings.demoMode": "Modo Demo",
    "settings.demoModeActive": "Modo Demo Activo",
    "settings.demoModeDescription":
      "Actualmente estás explorando la aplicación con datos de muestra. Tus cambios no se guardarán y se restablecerán cuando cierres el navegador.",
    "settings.exitDemoMode": "Salir del Modo Demo",
    "settings.clearFinancialData": "Borrar Datos Financieros",
    "settings.clearAllData": "Borrar Todos los Datos",
    "settings.clearFinancialDataDescription":
      "Esto borrará todos tus datos financieros (transacciones, ingresos, gastos, activos, deudas) pero mantendrá tu perfil de usuario.",
    "settings.clearAllDataDescription":
      "Esto borrará todos los datos incluyendo tu perfil de usuario. Necesitarás empezar completamente de nuevo.",

    // FIRE
    "fire.title": "Calculadora FIRE",
    "fire.description":
      "Calcula tu camino hacia la Independencia Financiera, Jubilación Anticipada (FIRE)",
    "fire.resetToDashboard": "Restablecer al Panel",

    // Partner
    "partner.title": "Gestión de Socios",
    "partner.description": "Gestiona socios financieros y colaboradores",
    "partner.addPartner": "Agregar Socio",

    // Projections
    "projections.title": "Proyecciones Financieras",
    "projections.description":
      "Explora diferentes escenarios financieros y su impacto en tu pista",

    // Language names
    "language.english": "English",
    "language.spanish": "Español",
  },
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("runway-language");
    return (saved as Language) || "en";
  });

  useEffect(() => {
    localStorage.setItem("runway-language", language);
  }, [language]);

  const t = (key: string): string => {
    return (translations[language] as Record<string, string>)[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
